import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import LoadThemeComponent from '../../../../base/LoadThemeComponent';
import trackQuickPickButtonClicked from '../../../../base/trackers/helpers';

import { generateRandomLine, numberArray } from '../../../../utils/helpers';
import { createInteractiveProps } from '../../../common/DivButton';
import { LineNumbers, PickedNumber, SelectSignalPick } from '../../Types/Types';
import { BONUS_NUMBERS, isPickValidLine } from '../../helpers/linesHelper';
import { RootState } from '../../redux/store';

import { setPickedNumbers } from '../../redux/storeSlice';

import LinePrice from './LinePrice';

import useLinesHelper from 'components/play/helpers/useLinesHelper';

import { isPremierDubai } from 'utils/template';

type Props = Readonly<{
    index: number;
    numbers: LineNumbers;
    numbersToPick: number;
    stake: number;
    setSelectSignal?: (value: SelectSignalPick) => void;
}>;

function Line({ index, numbers, numbersToPick, stake, setSelectSignal }: Props) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { stakeMin, stakeMax, linePriceGross } = useSelector((state: RootState) => state.playStore);
    const { lottery, lines, setLineStake, setLine, removeLine, removeStake } = useLinesHelper();

    function onClickQuickPick(rowIndex: number) {
        const randomLine = generateRandomLine(lottery.lotteryRule.ranges, numbersToPick);

        // if we want to quick pick last line and max lines are selected
        if (lottery.maxLinesPerTicket === lines.length && rowIndex === lottery.maxLinesPerTicket) {
            const pickedNumbers: Array<PickedNumber> = [];

            randomLine.forEach((value, pool) => {
                pickedNumbers.push({
                    value,
                    pool,
                });
            });
            dispatch(setPickedNumbers(pickedNumbers));
        } else {
            setLine(rowIndex, randomLine);
            setLineStake(rowIndex, Math.max(1.0, stakeMin));
        }
    }

    function onClickClear(rowIndex: number) {
        removeStake(rowIndex);
        removeLine(rowIndex);
    }

    const baseView = () => (
        <div
            key={index}
            className="mb-1 purchase-line-container-mobile mobile-picker row add-line line-chosen mobile-picker-checked"
        >
            <div className="col-auto text-ok">{t('playPicker.ok')}</div>

            <div className="col">
                <div className="row-balls-container">
                    <div className="row mobile-new-line">
                        {numbers.map((number, i) => (
                            <div key={i} className="ball">
                                {number}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="col-auto text-center">
                <button
                    type="button"
                    className="btn btn-primary mx-2 quick-pick-mobile"
                    onClick={() => {
                        onClickQuickPick(index);
                        trackQuickPickButtonClicked(lottery.slug);
                    }}
                >
                    {isPremierDubai() ? t('playPicker.autoChoose') : t('playPicker.quickPick')}
                </button>
                <button
                    type="button"
                    className="btn btn-secondary mx-2 mobile-delete-line"
                    onClick={() => onClickClear(index)}
                >
                    {t('playPicker.clear')}
                </button>
            </div>

            <LinePrice
                index={index}
                stake={stake}
                stakeMin={stakeMin}
                stakeMax={stakeMax}
                linePriceGross={linePriceGross}
                ticketPriceMin={null}
                ticketPriceMax={null}
                setLineStake={setLineStake}
            />
        </div>
    );

    const checked = isPickValidLine(numbers, numbersToPick);

    const renderPickerCheckedView = () => (
        <div className="row purchase-line-container-mobile fade-in mobile-picker-checked">
            <div className="row-balls-container checked d-flex flex-row justify-content-between align-items-center g-0 w-auto position-relative">
                <div className="mobile-new-line d-flex flex-wrap">
                    {numbers.map((number, i) => (
                        <div
                            key={i}
                            className="ball"
                            onClick={() => setSelectSignal({ clickSource: 'checkedView', lineIndex: index })}
                        >
                            {number}
                        </div>
                    ))}
                </div>
                <LinePrice
                    index={index}
                    stake={stake}
                    stakeMin={stakeMin}
                    stakeMax={stakeMax}
                    linePriceGross={linePriceGross}
                    ticketPriceMin={null}
                    ticketPriceMax={null}
                    setLineStake={setLineStake}
                />
            </div>
            <div className="row-balls-container-right position-relative w-auto g-0">
                <i
                    className="fas fa-trash mobile-delete-line"
                    {...createInteractiveProps(() => removeLine(index))}
                    aria-label={t('playPicker.clear')}
                />
            </div>
        </div>
    );

    const renderPickerUncheckedView = () => (
        <div className="row purchase-line-container-mobile fade-in">
            <div className="title-container position-relative g-0">
                <h1 className="title text-center">
                    <span className="d-block d-sm-inline"> {t('playPicker.pickNNumbers', { numbersToPick })} {t('playPicker.oneFromEachLine')}</span>
                </h1>
            </div>
            <div className="row-balls-container d-flex flex-column">
                <div className="mobile-new-line unchecked d-flex justify-content-center">
                    {numberArray(numbersToPick).map((i) => (
                        <div
                            key={i}
                            className="ball"
                            onClick={() => setSelectSignal({ clickSource: 'uncheckedView', lineIndex: 0 })}
                        />
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => onClickQuickPick(index)}
                    className="btn btn-primary button quick-pick-mobile mx-auto"
                >
                    {t('playPicker.quickPick')}
                </button>
            </div>
        </div>
    );

    const defaultView = () => <>{checked ? renderPickerCheckedView() : renderPickerUncheckedView()}</>;

    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}

export default Line;
